import React, { useState } from 'react';
import {
  WorkflowRule,
  WorkflowAction,
  FieldUpdateConfig,
  EmailAlertConfig,
  TaskConfig,
  NotificationConfig,
  CreateRecordConfig,
  WebhookConfig,
  FieldMapping,
  EmailRecipient,
  NotificationRecipient
} from '../../../../types/workflowRules';
import { Plus, Trash2, ChevronDown, ChevronUp, GripVertical, Mail, Edit3, CheckSquare, Bell, FileText, Globe } from 'lucide-react';
import { getObjectFieldsFromManager } from '../../../../utils/workflowFieldHelper';
import { StandardButton, StandardEmptyState, StandardSelect, StandardInput, StandardTextarea, StandardCheckbox } from '../../../standard';

interface WizardStep3Props {
  formData: Partial<WorkflowRule>;
  updateFormData: (updates: Partial<WorkflowRule>) => void;
  onNext: () => void;
  onBack: () => void;
}

/**
 * WizardStep3Actions Component
 *
 * Actions builder allowing users to configure automated actions when workflow criteria are met.
 * Supports 6 action types: Field Update, Email Alert, Task Creation, Notification, Create Record, Call Webhook
 *
 * @component
 */
export const WizardStep3Actions: React.FC<WizardStep3Props> = ({
  formData,
  updateFormData
}) => {
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());

  const actions = formData.actions || [];

  // Mock data for dropdowns
  const emailTemplates = [
    { id: 'template_1', name: 'Welcome Email' },
    { id: 'template_2', name: 'Case Created Notification' },
    { id: 'template_3', name: 'Task Assignment Alert' },
    { id: 'template_4', name: 'Status Update Notification' }
  ];

  const users = [
    { id: 'user_1', name: 'John Smith' },
    { id: 'user_2', name: 'Sarah Johnson' },
    { id: 'user_3', name: 'Mike Davis' },
    { id: 'user_4', name: 'Admin User' }
  ];

  // Get fields from Object Manager
  const objectFields = formData.objectApiName
    ? getObjectFieldsFromManager(formData.objectApiName)
    : [];

  // Get next order number
  const getNextOrder = (): number => {
    if (actions.length === 0) return 1;
    return Math.max(...actions.map(a => a.order)) + 1;
  };

  // Add action
  const handleAddAction = (type: WorkflowAction['type']) => {
    const newAction: WorkflowAction = {
      id: `action_${Date.now()}`,
      type,
      order: getNextOrder(),
      config: getDefaultConfig(type)
    };

    updateFormData({
      actions: [...actions, newAction]
    });

    // Auto-expand the new action
    const newSet = new Set(expandedActions);
    newSet.add(newAction.id);
    setExpandedActions(newSet);
    setShowActionMenu(false);
  };

  // Get default config for action type
  const getDefaultConfig = (type: WorkflowAction['type']): WorkflowAction['config'] => {
    switch (type) {
      case 'fieldUpdate':
        return {
          fieldApiName: '',
          newValue: ''
        } as FieldUpdateConfig;
      case 'emailAlert':
        return {
          templateId: '',
          recipients: []
        } as EmailAlertConfig;
      case 'task':
        return {
          subject: '',
          assignedTo: '',
          dueDate: { type: 'relative', days: 7 },
          priority: 'Normal',
          status: 'Not Started'
        } as TaskConfig;
      case 'notification':
        return {
          message: '',
          recipients: [],
          priority: 'Normal'
        } as NotificationConfig;
      case 'createRecord':
        return {
          objectApiName: '',
          fieldMappings: [],
          linkToTriggerRecord: false
        } as CreateRecordConfig;
      case 'webhook':
        return {
          url: '',
          method: 'POST',
          headers: {},
          includeRecordData: true
        } as WebhookConfig;
    }
  };

  // Remove action
  const handleRemoveAction = (actionId: string) => {
    updateFormData({
      actions: actions.filter(a => a.id !== actionId)
    });
    const newSet = new Set(expandedActions);
    newSet.delete(actionId);
    setExpandedActions(newSet);
  };

  // Toggle expand/collapse
  const toggleExpand = (actionId: string) => {
    const newSet = new Set(expandedActions);
    if (newSet.has(actionId)) {
      newSet.delete(actionId);
    } else {
      newSet.add(actionId);
    }
    setExpandedActions(newSet);
  };

  // Update specific action config
  const updateActionConfig = (actionId: string, newConfig: WorkflowAction['config']) => {
    updateFormData({
      actions: actions.map(action =>
        action.id === actionId
          ? { ...action, config: newConfig }
          : action
      )
    });
  };

  // Get action type info
  const getActionTypeInfo = (type: WorkflowAction['type']) => {
    const types = {
      fieldUpdate: {
        icon: Edit3,
        label: 'Field Update',
        description: 'Automatically update field values',
        color: 'blue'
      },
      emailAlert: {
        icon: Mail,
        label: 'Email Alert',
        description: 'Send email notifications',
        color: 'green'
      },
      task: {
        icon: CheckSquare,
        label: 'Create Task',
        description: 'Create and assign tasks',
        color: 'purple'
      },
      notification: {
        icon: Bell,
        label: 'Send Notification',
        description: 'Send in-app notifications',
        color: 'orange'
      },
      createRecord: {
        icon: FileText,
        label: 'Create Record',
        description: 'Create a new related record',
        color: 'indigo'
      },
      webhook: {
        icon: Globe,
        label: 'Call Webhook',
        description: 'Send data to external system',
        color: 'pink'
      }
    };
    return types[type as keyof typeof types];
  };

  // Helper to check if recipient is in list
  const hasEmailRecipient = (recipients: EmailRecipient[], recipientType: 'recordOwner'): boolean => {
    return recipients.some(r => r.type === recipientType);
  };

  const hasNotificationRecipient = (recipients: NotificationRecipient[], recipientType: 'recordOwner'): boolean => {
    return recipients.some(r => r.type === recipientType);
  };

  // Render action configuration form
  const renderActionConfig = (action: WorkflowAction) => {
    const isExpanded = expandedActions.has(action.id);
    if (!isExpanded) return null;

    switch (action.type) {
      case 'fieldUpdate': {
        const config = action.config as FieldUpdateConfig;
        const selectedField = objectFields.find(f => f.apiName === config.fieldApiName);
        const isPicklist = selectedField?.type === 'picklist';

        // Get picklist options based on field
        const getPicklistOptions = (fieldApiName: string): string[] => {
          if (fieldApiName === 'status') {
            return ['New', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
          } else if (fieldApiName === 'priority') {
            return ['Low', 'Normal', 'High', 'Urgent'];
          }
          return [];
        };

        const picklistOptions = isPicklist && config.fieldApiName ? getPicklistOptions(config.fieldApiName) : [];

        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardSelect
              label="Field to Update"
              required
              value={config.fieldApiName || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, fieldApiName: e.target.value })}
              options={[
                { value: '', label: 'Select a field...' },
                ...objectFields.map(field => ({ value: field.apiName, label: field.label }))
              ]}
            />

            {isPicklist ? (
              <StandardSelect
                label="New Value"
                required
                value={config.newValue || ''}
                onChange={(e) => updateActionConfig(action.id, { ...config, newValue: e.target.value })}
                options={[
                  { value: '', label: 'Select a value...' },
                  ...picklistOptions.map(option => ({ value: option, label: option }))
                ]}
              />
            ) : (
              <StandardInput
                label="New Value"
                required
                value={config.newValue || ''}
                onChange={(e) => updateActionConfig(action.id, { ...config, newValue: e.target.value })}
                placeholder="Enter new value"
              />
            )}

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                This will update the selected field to the specified value when the workflow runs.
              </p>
            </div>
          </div>
        );
      }

      case 'emailAlert': {
        const config = action.config as EmailAlertConfig;

        // Helper to check if user recipient exists
        const hasUserRecipient = (recipients: EmailRecipient[], userId: string): boolean => {
          return recipients.some(r => r.type === 'user' && r.userId === userId);
        };

        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardSelect
              label="Email Template"
              required
              value={config.templateId || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, templateId: e.target.value })}
              options={[
                { value: '', label: 'Select a template...' },
                ...emailTemplates.map(template => ({ value: template.id, label: template.name }))
              ]}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recipients <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                {/* Record Owner */}
                <StandardCheckbox
                  checked={hasEmailRecipient(config.recipients, 'recordOwner')}
                  onChange={(checked) => {
                    const newRecipients = checked
                      ? [...config.recipients, { type: 'recordOwner' as const }]
                      : config.recipients.filter(r => r.type !== 'recordOwner');
                    updateActionConfig(action.id, { ...config, recipients: newRecipients });
                  }}
                  label="Record Owner"
                />

                {/* Individual Users */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Specific Users:</div>
                  {users.map(user => (
                    <StandardCheckbox
                      key={user.id}
                      checked={hasUserRecipient(config.recipients, user.id)}
                      onChange={(checked) => {
                        const newRecipients = checked
                          ? [...config.recipients, { type: 'user' as const, userId: user.id }]
                          : config.recipients.filter(r => !(r.type === 'user' && r.userId === user.id));
                        updateActionConfig(action.id, { ...config, recipients: newRecipients });
                      }}
                      label={user.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-800 dark:text-green-200">
                An email will be sent using the selected template to the specified recipients when the workflow runs.
              </p>
            </div>
          </div>
        );
      }

      case 'task': {
        const config = action.config as TaskConfig;
        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardInput
              label="Subject"
              required
              value={config.subject || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, subject: e.target.value })}
              placeholder="Enter task subject"
            />

            <StandardSelect
              label="Assign To"
              required
              value={config.assignedTo || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, assignedTo: e.target.value })}
              options={[
                { value: '', label: 'Select user...' },
                { value: 'recordOwner', label: 'Record Owner' },
                ...users.map(user => ({ value: user.id, label: user.name }))
              ]}
            />

            <div className="grid grid-cols-2 gap-4">
              <StandardSelect
                label="Due Date Type"
                value={config.dueDate.type}
                onChange={(e) => {
                  const newDueDate = e.target.value === 'relative'
                    ? { type: 'relative' as const, days: 7 }
                    : { type: 'absolute' as const, date: new Date().toISOString().split('T')[0] };
                  updateActionConfig(action.id, { ...config, dueDate: newDueDate });
                }}
                options={[
                  { value: 'relative', label: 'Relative (days from now)' },
                  { value: 'absolute', label: 'Specific Date' }
                ]}
              />

              {config.dueDate.type === 'relative' ? (
                <StandardInput
                  label="Days from now"
                  type="number"
                  value={config.dueDate.days}
                  onChange={(e) => updateActionConfig(action.id, {
                    ...config,
                    dueDate: { type: 'relative', days: parseInt(e.target.value) || 0 }
                  })}
                />
              ) : (
                <StandardInput
                  label="Specific Date"
                  type="date"
                  value={config.dueDate.date}
                  onChange={(e) => updateActionConfig(action.id, {
                    ...config,
                    dueDate: { type: 'absolute', date: e.target.value }
                  })}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <StandardSelect
                label="Priority"
                value={config.priority}
                onChange={(e) => updateActionConfig(action.id, {
                  ...config,
                  priority: e.target.value as 'Low' | 'Normal' | 'High' | 'Urgent'
                })}
                options={[
                  { value: 'Low', label: 'Low' },
                  { value: 'Normal', label: 'Normal' },
                  { value: 'High', label: 'High' },
                  { value: 'Urgent', label: 'Urgent' }
                ]}
              />

              <StandardSelect
                label="Status"
                value={config.status || 'Not Started'}
                onChange={(e) => updateActionConfig(action.id, { ...config, status: e.target.value })}
                options={[
                  { value: 'Not Started', label: 'Not Started' },
                  { value: 'In Progress', label: 'In Progress' },
                  { value: 'Completed', label: 'Completed' }
                ]}
              />
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <p className="text-sm text-purple-800 dark:text-purple-200">
                A new task will be created and assigned when the workflow runs.
              </p>
            </div>
          </div>
        );
      }

      case 'notification': {
        const config = action.config as NotificationConfig;
        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardTextarea
              label="Notification Message"
              required
              value={config.message || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, message: e.target.value })}
              placeholder="Enter notification message"
              rows={3}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recipients <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <StandardCheckbox
                  checked={hasNotificationRecipient(config.recipients, 'recordOwner')}
                  onChange={(checked) => {
                    const newRecipients = checked
                      ? [...config.recipients, { type: 'recordOwner' as const }]
                      : config.recipients.filter(r => r.type !== 'recordOwner');
                    updateActionConfig(action.id, { ...config, recipients: newRecipients });
                  }}
                  label="Record Owner"
                />

                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  More recipient options (users, roles) can be added in future enhancements
                </div>
              </div>
            </div>

            <StandardSelect
              label="Priority"
              value={config.priority}
              onChange={(e) => updateActionConfig(action.id, {
                ...config,
                priority: e.target.value as 'Low' | 'Normal' | 'High'
              })}
              options={[
                { value: 'Low', label: 'Low' },
                { value: 'Normal', label: 'Normal' },
                { value: 'High', label: 'High' }
              ]}
            />

            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                An in-app notification will be sent to the specified recipients when the workflow runs.
              </p>
            </div>
          </div>
        );
      }

      case 'createRecord': {
        const config = action.config as CreateRecordConfig;
        const objectTypes = ['Case', 'Task', 'Contact', 'Lead', 'Opportunity'];

        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardSelect
              label="Object Type"
              required
              value={config.objectApiName || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, objectApiName: e.target.value })}
              options={[
                { value: '', label: 'Select object type...' },
                ...objectTypes.map(obj => ({ value: obj, label: obj }))
              ]}
            />

            {/* Field Mappings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Mappings
              </label>
              {config.fieldMappings.map((mapping, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <StandardInput
                    value={mapping.fieldApiName}
                    onChange={(e) => {
                      const newMappings = [...config.fieldMappings];
                      newMappings[index] = { ...mapping, fieldApiName: e.target.value };
                      updateActionConfig(action.id, { ...config, fieldMappings: newMappings });
                    }}
                    placeholder="Field Name"
                    className="flex-1"
                  />
                  <StandardSelect
                    value={mapping.valueType}
                    onChange={(e) => {
                      const newMappings = [...config.fieldMappings];
                      newMappings[index] = { ...mapping, valueType: e.target.value as 'static' | 'triggerField' };
                      updateActionConfig(action.id, { ...config, fieldMappings: newMappings });
                    }}
                    options={[
                      { value: 'static', label: 'Static Value' },
                      { value: 'triggerField', label: 'From Trigger Record' }
                    ]}
                  />
                  <StandardInput
                    value={mapping.value}
                    onChange={(e) => {
                      const newMappings = [...config.fieldMappings];
                      newMappings[index] = { ...mapping, value: e.target.value };
                      updateActionConfig(action.id, { ...config, fieldMappings: newMappings });
                    }}
                    placeholder={mapping.valueType === 'static' ? 'Value' : 'Field API Name'}
                    className="flex-1"
                  />
                  <StandardButton
                    variant="ghost"
                    onClick={() => {
                      const newMappings = config.fieldMappings.filter((_, i) => i !== index);
                      updateActionConfig(action.id, { ...config, fieldMappings: newMappings });
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </StandardButton>
                </div>
              ))}
              <button
                onClick={() => {
                  const newMapping: FieldMapping = { fieldApiName: '', valueType: 'static', value: '' };
                  updateActionConfig(action.id, { ...config, fieldMappings: [...config.fieldMappings, newMapping] });
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                + Add Field Mapping
              </button>
            </div>

            <StandardCheckbox
              checked={config.linkToTriggerRecord || false}
              onChange={(checked) => updateActionConfig(action.id, { ...config, linkToTriggerRecord: checked })}
              label="Link to trigger record"
            />
          </div>
        );
      }

      case 'webhook': {
        const config = action.config as WebhookConfig;

        return (
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardInput
              label="Webhook URL"
              required
              type="url"
              value={config.url || ''}
              onChange={(e) => updateActionConfig(action.id, { ...config, url: e.target.value })}
              placeholder="https://api.example.com/webhook"
            />

            <StandardSelect
              label="HTTP Method"
              value={config.method}
              onChange={(e) => updateActionConfig(action.id, { ...config, method: e.target.value as 'GET' | 'POST' | 'PUT' | 'PATCH' })}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'PATCH', label: 'PATCH' }
              ]}
            />

            {(config.method === 'POST' || config.method === 'PUT' || config.method === 'PATCH') && (
              <StandardTextarea
                label="Request Body (JSON)"
                value={config.body || ''}
                onChange={(e) => updateActionConfig(action.id, { ...config, body: e.target.value })}
                placeholder={'{\n  "field": "{{fieldName}}",\n  "value": "{{value}}"\n}'}
                rows={6}
                helpText="Use {{fieldName}} to reference record fields"
              />
            )}

            <StandardCheckbox
              checked={config.includeRecordData || false}
              onChange={(checked) => updateActionConfig(action.id, { ...config, includeRecordData: checked })}
              label="Include full record data in request"
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Configure Actions
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Define what should happen when criteria are met
        </p>
      </div>

      {/* Actions List */}
      {actions.length > 0 && (
        <div className="space-y-3">
          {actions.map((action) => {
            const typeInfo = getActionTypeInfo(action.type);
            const Icon = typeInfo.icon;
            const isExpanded = expandedActions.has(action.id);

            return (
              <div
                key={action.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                {/* Action Header */}
                <div className="flex items-center gap-3">
                  <GripVertical className="w-5 h-5 text-gray-400 cursor-move flex-shrink-0" />

                  <div className={`w-8 h-8 rounded-lg bg-${typeInfo.color}-100 dark:bg-${typeInfo.color}-900/20 flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 text-${typeInfo.color}-600 dark:text-${typeInfo.color}-400`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        #{action.order}
                      </span>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {typeInfo.label}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {typeInfo.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleExpand(action.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>

                    <button
                      onClick={() => handleRemoveAction(action.id)}
                      className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                      title="Remove action"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Action Configuration */}
                {renderActionConfig(action)}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {actions.length === 0 && (
        <StandardEmptyState
          icon={<Plus className="w-16 h-16" />}
          title="No Actions Yet"
          message="Add actions to define what happens when your workflow criteria are met"
        />
      )}

      {/* Add Action Button/Menu */}
      <div className="relative">
        <StandardButton
          variant="primary"
          leftIcon={<Plus className="w-5 h-5" />}
          onClick={() => setShowActionMenu(!showActionMenu)}
          className="w-full justify-center"
        >
          Add Action
        </StandardButton>

        {/* Action Type Menu */}
        {showActionMenu && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
            <div className="p-2 space-y-1">
              {/* Field Update */}
              <button
                onClick={() => handleAddAction('fieldUpdate')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <Edit3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Field Update</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically update field values
                  </div>
                </div>
              </button>

              {/* Email Alert */}
              <button
                onClick={() => handleAddAction('emailAlert')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Email Alert</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Send email notifications to users
                  </div>
                </div>
              </button>

              {/* Create Task */}
              <button
                onClick={() => handleAddAction('task')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Create Task</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Create and assign tasks automatically
                  </div>
                </div>
              </button>

              {/* Send Notification */}
              <button
                onClick={() => handleAddAction('notification')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Send Notification</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Send in-app notifications
                  </div>
                </div>
              </button>

              {/* Create Record */}
              <button
                onClick={() => handleAddAction('createRecord')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Create Record</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Create a new related record
                  </div>
                </div>
              </button>

              {/* Call Webhook */}
              <button
                onClick={() => handleAddAction('webhook')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-900/20 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Call Webhook</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Send data to external system via HTTP
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <div className="font-medium mb-2">Action Execution Order</div>
          <div>
            Actions will be executed in the order shown above. You can add multiple actions of the same type.
            Each action is processed sequentially when the workflow criteria are met.
          </div>
        </div>
      </div>
    </div>
  );
};
